/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import { usePermissions } from '@/providers/Permissions.provider';
import { UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';

const { Box, Title, Description, Button, ElementContainer, TextField, DetailsCard } = vp;
type FC = FunctionComponent;

type ApproverItems = {
  PaymentRails: string;
  Amount: string;
  IsAny: boolean;
};
const ApprovalRolesContainer: FC = () => {
  const [userRolesResponse, fetchUserRoles] = useFetchUserRoles({});
  const [fetchAdmin, setFetchAdmin] = useState(true);
  const [adminUsers, setAdminUsers] = useState<vp.UserRolesResponse | undefined>();
  const [approverUsers, setApproverUsers] = useState<vp.UserRolesResponse | undefined>();
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Roles.List' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();
  const { permissions } = usePermissions();
  useEffect(() => {
    fetchUserRoles({
      Role: 'super admin',
    });
  }, []);
  useEffect(() => {
    if (userRolesResponse) {
      if (fetchAdmin) {
        fetchUserRoles({
          Role: 'approver',
        });
        setAdminUsers({ ...userRolesResponse });
        setFetchAdmin(false);
      } else {
        setApproverUsers({ ...userRolesResponse });
      }
    }
  }, [userRolesResponse]);
  // Group the data by 'User'
  const groupedApprovers = Object.values(approverUsers?.UserRolePermissions || {}).reduce(
    (acc, approver) => {
      const user = approver.Username;

      if (!acc[user]) {
        acc[user] = [];
      }

      acc[user].push({
        PaymentRails: approver.TransactionType,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        Amount: approver.MaxAmount ? approver.MaxAmount.toString() : '0',
        IsAny: approver.MaxAmount === null,
      });

      return acc;
    },
    {} as { [key: string]: ApproverItems[] },
  );

  return (
    <div>
      <Box flx="col" mb="md" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/transaction-approval/configuration');
            }
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
        </UtilButton>

        <Title title={t('title')} />
      </Box>
      <Box mt="md" mb="md" gap="md" jc="sb">
        <Description>{t('instructions')}</Description>
        {permissions.CreateTransactionApprovalWorkflow && (
          <Button
            id="approval-update-roles"
            label={t('editButton')}
            color="primary"
            onClick={() => {
              history.push('/transaction-approval/configuration/roles/update');
            }}
          />
        )}
      </Box>

      <Box maxwidth="480px" flx="col">
        <ElementContainer
          label={t('SuperAdminBlock.label', { count: Object.values(adminUsers?.UserRolePermissions || {}).length })}
          padding="10px 0 15px"
          input={
            <Box flx="col" width="100%">
              {Object.values(adminUsers?.UserRolePermissions || {}).map((user, index) => (
                <Box pb="sm" width="100%">
                  <TextField
                    width="100%"
                    containerWidth="100%"
                    disabled
                    variant="background"
                    value={user.Username}
                    name={`admin-${index}`}
                    onBlur={() => {}}
                    onChange={() => {}}
                  />
                </Box>
              ))}
            </Box>
          }
        />
      </Box>

      <Box flx="col" mb="lg">
        <ElementContainer
          label={t('ApproverBlock.label', {
            count: [
              ...new Set(Object.values(approverUsers?.UserRolePermissions || {}).map((approver) => approver.Username)),
            ].length,
          })}
          padding="20px 0 15px"
          input={
            <Box flx="col" width="100%">
              {[
                ...new Set(
                  Object.values(approverUsers?.UserRolePermissions || {}).map((approver) => approver.Username),
                ),
              ].map((user, index) => (
                <Box pt="sm" flx="col" width="100%">
                  <DetailsCard
                    content={
                      <Box flx="col" gap="sm" maxwidth="940px">
                        <Box gap="common" flx="row" ai="center" jc="sb">
                          <TextField
                            width="100%"
                            containerWidth="100%"
                            disabled
                            variant="background"
                            value={user || ''}
                            name={`approver-${index}`}
                            onBlur={() => {}}
                            onChange={() => {}}
                          />
                          <div style={{ width: '100%' }} />
                        </Box>
                        {groupedApprovers[user].map((groupedApprover, indexGroup) => (
                          <Box gap="common" flx="row" ai="center" jc="sb">
                            <ElementContainer
                              width="100%"
                              label={indexGroup === 0 ? t('ApproverBlock.paymentRailsLabel') : ''}
                              padding={indexGroup === 0 ? '10px 0 8px' : '0px 0 0px'}
                              input={
                                <TextField
                                  width="100%"
                                  containerWidth="100%"
                                  disabled
                                  variant="background"
                                  value={groupedApprover.PaymentRails || ''}
                                  name={`approver-${index}`}
                                  onBlur={() => {}}
                                  onChange={() => {}}
                                />
                              }
                            />
                            <ElementContainer
                              label={indexGroup === 0 ? t('ApproverBlock.maximumApprovalAmountLabel') : ''}
                              width="100%"
                              padding={indexGroup === 0 ? '10px 0 8px' : '0px 0 0px'}
                              input={
                                <TextField
                                  width="100%"
                                  containerWidth="100%"
                                  disabled
                                  variant="background"
                                  value={
                                    groupedApprover.IsAny
                                      ? t('ApproverBlock.anyValue')
                                      : `$${vp.formattedCurrency(groupedApprover.Amount)}`
                                  }
                                  name={`approver-${index}`}
                                  onBlur={() => {}}
                                  onChange={() => {}}
                                />
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    }
                  />
                </Box>
              ))}
            </Box>
          }
        />
      </Box>
    </div>
  );
};
export default ApprovalRolesContainer;
